import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, ArrowRight, Save, Feather, Mic, MicOff, Volume2, X, Sparkles, PanelRightClose, ChevronDown, ChevronUp, FileText, Loader2, RefreshCw, Lightbulb, Target, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import AppShell from '@/components/AppShell';
import WordCountBar from '@/components/WordCountBar';
import FreezeDetector from '@/components/FreezeDetector';
import ChapterCoverage from '@/components/ChapterCoverage';
import PolishStyles from '@/components/PolishStyles';
import ChapterSidebar from '@/components/ChapterSidebar';
import QuickNotes from '@/components/QuickNotes';
import VoiceWaveform from '@/components/VoiceWaveform';
import { useVoiceFeatures } from '@/components/VoiceControls';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ChapterEditorPage() {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const textareaRef = useRef(null);

  // Core state
  const [chapter, setChapter] = useState(null);
  const [book, setBook] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Polish state
  const [polishing, setPolishing] = useState(false);
  const [polishedContent, setPolishedContent] = useState(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [editorialSuggestions, setEditorialSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [polishedFromSnapshot, setPolishedFromSnapshot] = useState('');
  const [polishState, setPolishState] = useState('none'); // 'none' | 'fresh' | 'outdated'

  // Ignite state
  const [showIgniteModal, setShowIgniteModal] = useState(false);
  const [igniteData, setIgniteData] = useState(null);
  const [loadingIgnite, setLoadingIgnite] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);

  // Voice features
  const {
    isListening: isRecording,
    isSpeaking: isReading,
    isLoading: isLoadingVoice,
    startDeepgramListening,
    stopDeepgramListening,
    stopWhisperListening,
    primeAudio,
    interimTranscript,
    isTranscribing: isTranscribingVoice,
    isRecordedMode,
    speak,
    stopSpeaking,
    audioLevel
  } = useVoiceFeatures();

  // UI state
  const [showSidebar, setShowSidebar] = useState(false);
  const [showPolishPanel, setShowPolishPanel] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [mobilePolishOpen, setMobilePolishOpen] = useState(false);
  const [highlightQuery, setHighlightQuery] = useState(null);
  const [lastSavedContent, setLastSavedContent] = useState('');
  const [showSavedBanner, setShowSavedBanner] = useState(false);
  const [headerExpanded, setHeaderExpanded] = useState(true);
  const [selectionToolbar, setSelectionToolbar] = useState({ visible: false, x: 0, y: 0 });

  // Auto-save ref to track if content changed
  const contentRef = useRef(content);
  const hasUnsavedChanges = useRef(false);
  const lastVoiceChunkRef = useRef('');

  useEffect(() => {
    contentRef.current = content;
    if (content !== lastSavedContent) {
      hasUnsavedChanges.current = true;
    }
    if (polishState === 'fresh' && polishedFromSnapshot && content !== polishedFromSnapshot) {
      setPolishState('outdated');
    }
  }, [content, lastSavedContent, polishState, polishedFromSnapshot]);

  const autoSave = useCallback(async () => {
    if (!hasUnsavedChanges.current || !chapterId) return;
    try {
      const saveData = { content: contentRef.current, notes };
      if (!originalContent && contentRef.current.trim()) {
        saveData.original_content = contentRef.current;
      }
      await axios.put(`${API}/chapters/${chapterId}`, saveData);
      setLastSavedContent(contentRef.current);
      hasUnsavedChanges.current = false;
      setShowSavedBanner(true);
      setTimeout(() => setShowSavedBanner(false), 2000);
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  }, [chapterId, notes, originalContent]);

  const saveAndNavigate = useCallback(async (to) => {
    if (hasUnsavedChanges.current && chapterId) {
      try {
        const saveData = { content: contentRef.current, notes };
        if (!originalContent && contentRef.current.trim()) {
          saveData.original_content = contentRef.current;
        }
        await axios.put(`${API}/chapters/${chapterId}`, saveData);
        setLastSavedContent(contentRef.current);
        hasUnsavedChanges.current = false;
        setShowSavedBanner(true);
        setTimeout(() => {
          setShowSavedBanner(false);
          navigate(to);
        }, 800);
      } catch (error) {
        console.error('Auto-save before navigation failed:', error);
        navigate(to);
      }
    } else {
      navigate(to);
    }
  }, [chapterId, notes, originalContent, navigate]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges.current) {
        autoSave();
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [autoSave]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (hasUnsavedChanges.current) autoSave();
    }, 30000);
    return () => clearInterval(interval);
  }, [autoSave]);

  const loadChapter = useCallback(async () => {
    try {
      const chapterRes = await axios.get(`${API}/chapters/${chapterId}`);
      const chapterData = chapterRes.data;
      setChapter(chapterData);
      setContent(chapterData.content || '');
      setLastSavedContent(chapterData.content || '');
      setOriginalContent(chapterData.original_content || chapterData.content || '');
      setNotes(chapterData.notes || []);
      setPolishedContent(chapterData.polished_content || null);
      hasUnsavedChanges.current = false;
      const [bookRes, chaptersRes] = await Promise.all([
        axios.get(`${API}/books/${chapterData.book_id}`),
        axios.get(`${API}/books/${chapterData.book_id}/chapters`)
      ]);
      setBook(bookRes.data);
      setChapters(chaptersRes.data);
    } catch (error) {
      console.error('Failed to load chapter:', error);
      toast.error('Failed to load chapter');
    } finally {
      setLoading(false);
    }
  }, [chapterId]);

  useEffect(() => {
    loadChapter();
    if (location.state?.searchQuery) setHighlightQuery(location.state.searchQuery);
    window.scrollTo(0, 0);
  }, [chapterId, location.state, loadChapter]);

  useEffect(() => {
    return () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, []);

  useEffect(() => {
    if (highlightQuery && content && textareaRef.current) {
      const textarea = textareaRef.current;
      const index = content.toLowerCase().indexOf(highlightQuery.toLowerCase());
      if (index !== -1) {
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(index, index + highlightQuery.length);
          toast.success(`Found "${highlightQuery}"`, { description: 'Text highlighted in editor' });
        }, 500);
        setTimeout(() => setHighlightQuery(null), 3000);
      }
    }
  }, [highlightQuery, content]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const saveData = { content, notes };
      if (!originalContent && content.trim()) {
        saveData.original_content = content;
        setOriginalContent(content);
      }
      await axios.put(`${API}/chapters/${chapterId}`, saveData);
      setLastSavedContent(content);
      hasUnsavedChanges.current = false;
      toast.success('Chapter saved!');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save chapter');
    } finally {
      setSaving(false);
    }
  };

  const handleNotesSave = async (updatedNotes) => {
    setNotes(updatedNotes);
    try {
      await axios.put(`${API}/chapters/${chapterId}`, { notes: updatedNotes });
    } catch (error) {
      console.error('Failed to save notes:', error);
    }
  };

  const handleIgnite = async () => {
    setLoadingIgnite(true);
    try {
      const response = await axios.post(`${API}/chapters/${chapterId}/ignite`, {});
      setIgniteData(response.data);
      setShowIgniteModal(true);
      toast.success('🔥 Brain igniters generated!');
    } catch (error) {
      console.error('Failed to generate igniters:', error);
      if (error.response?.status !== 402) toast.error('Failed to generate brain igniters');
    } finally {
      setLoadingIgnite(false);
    }
  };

  const insertTextAtCursor = (text) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newContent = content.substring(0, start) + text + content.substring(end);
    setContent(newContent);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + text.length, start + text.length);
    }, 0);
    toast.success('Text inserted!');
  };

  const handlePolish = async (style, depth = 'medium') => {
    const textToPolish = originalContent || content;
    if (!textToPolish.trim()) { toast.error('Please write some content first'); return; }
    if (!originalContent && content.trim()) setOriginalContent(content);
    const currentDraft = content;
    setPolishing(true);
    setEditorialSuggestions([]);
    try {
      const response = await axios.post(`${API}/chapters/${chapterId}/polish`, {
        content: originalContent || content, style, depth, verify: false
      });
      setPolishedContent(response.data.polished_content);
      setPolishedFromSnapshot(currentDraft);
      setPolishState('fresh');
      const analysis = response.data.analysis;
      if (analysis?.chapter_type) {
        toast.success(`Polished as "${analysis.chapter_type}" chapter with ${style} style!`);
      } else {
        toast.success(`Polished with ${style} style!`);
      }
      let suggestionsSet = false;
      if (analysis?.gap_analysis?.shallow_sections?.length > 0) {
        const gapSuggestions = analysis.gap_analysis.shallow_sections.slice(0, 3).map(gap => ({
          quoted_text: gap.location || 'This section',
          why_this_moment_matters: gap.issue || 'Could use more detail',
          expansion_direction: gap.expansion_type || 'detail',
          narrative_reason: `Detected during ${analysis.chapter_type || 'chapter'} analysis`
        }));
        setEditorialSuggestions(gapSuggestions);
        suggestionsSet = true;
      }
      if (!suggestionsSet) fetchDepthSuggestions(originalContent || content);
    } catch (error) {
      console.error('Failed to polish:', error);
      toast.error('Failed to polish chapter');
    } finally {
      setPolishing(false);
    }
  };

  const fetchDepthSuggestions = async (originalText) => {
    setLoadingSuggestions(true);
    try {
      const response = await axios.post(`${API}/chapters/${chapterId}/analyze`, { content: originalText });
      if (response.data && response.data.length > 0) {
        const enrichedSuggestions = response.data.slice(0, 3).map(sugg => {
          const quoted = sugg.quoted_text || '';
          const index = originalText.indexOf(quoted);
          return { ...sugg, anchor_index: index, anchor_substring: quoted.substring(0, 40).toLowerCase(), source_text: originalText };
        });
        setEditorialSuggestions(enrichedSuggestions);
      }
    } catch (error) {
      console.error('Failed to get depth suggestions:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const fuzzyFindText = (searchText, fullText) => {
    let index = fullText.indexOf(searchText);
    if (index !== -1) return index;
    index = fullText.toLowerCase().indexOf(searchText.toLowerCase());
    if (index !== -1) return index;
    const anchor = searchText.substring(0, 30).toLowerCase();
    index = fullText.toLowerCase().indexOf(anchor);
    if (index !== -1) return index;
    const words = searchText.split(' ').filter(w => w.length > 4);
    for (const word of words) {
      index = fullText.toLowerCase().indexOf(word.toLowerCase());
      if (index !== -1) return index;
    }
    return -1;
  };

  const goToQuotedSection = (suggestion) => {
    const { quoted_text, expansion_direction, anchor_index, anchor_substring } = suggestion;
    const textToSearch = content;
    let index = -1;
    if (anchor_index !== undefined && anchor_index >= 0) {
      const checkText = textToSearch.substring(anchor_index, anchor_index + 50).toLowerCase();
      if (checkText.includes(anchor_substring?.substring(0, 20) || '')) index = anchor_index;
    }
    if (index === -1) index = textToSearch.indexOf(quoted_text);
    if (index === -1) index = fuzzyFindText(quoted_text, textToSearch);
    toast.info(`💡 ${expansion_direction}`, { duration: 8000 });
    if (index !== -1) {
      focusAndHighlight(index, quoted_text.length, textToSearch);
    } else {
      toast.warning('Could not locate exact text. Please find the section manually.');
      const textarea = document.querySelector('[data-testid="chapter-content-textarea"]');
      if (textarea) textarea.focus();
    }
    setEditorialSuggestions(prev => prev.filter(s => s !== suggestion));
  };

  const focusAndHighlight = (startIndex, length, fullText) => {
    const textarea = document.querySelector('[data-testid="chapter-content-textarea"]');
    if (!textarea) return;
    textarea.focus();
    textarea.setSelectionRange(startIndex, startIndex + length);
    const textBefore = fullText.substring(0, startIndex);
    const linesBefore = textBefore.split('\n').length;
    const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 28;
    textarea.scrollTop = Math.max(0, (linesBefore - 3) * lineHeight);
  };

  const usePolishedVersion = () => {
    if (polishedContent) { setContent(polishedContent); toast.success('Polished version applied!'); }
  };

  const handleAcceptAndNextChapter = async (polished) => {
    try {
      setContent(polished);
      await axios.put(`${API}/chapters/${chapterId}`, { content: polished, notes });
      const currentIndex = chapters.findIndex(c => c.chapter_id === chapterId);
      const nextChapter = chapters[currentIndex + 1];
      if (nextChapter) {
        toast.success('Chapter saved! Moving to next chapter...');
        setPolishedContent(null);
        navigate(`/chapters/${nextChapter.chapter_id}`);
      } else {
        toast.success('Chapter saved! That was the last chapter.');
        setPolishedContent(null);
        navigate(`/books/${book.book_id}`);
      }
    } catch (error) {
      console.error('Failed to save and navigate:', error);
      toast.error('Failed to save chapter');
    }
  };

  const getNextChapter = () => {
    const currentIndex = chapters.findIndex(c => c.chapter_id === chapterId);
    return chapters[currentIndex + 1] || null;
  };

  const appendVoiceTranscript = useCallback((transcript) => {
    const cleanTranscript = transcript?.trim();
    if (!cleanTranscript) return;
    if (cleanTranscript === lastVoiceChunkRef.current) return;
    lastVoiceChunkRef.current = cleanTranscript;
    setContent(prev => prev + (prev?.trim() ? ' ' : '') + cleanTranscript);
  }, []);

  const startRecording = () => {
    lastVoiceChunkRef.current = '';
    primeAudio?.();
    startDeepgramListening(appendVoiceTranscript, { whisperAutoStop: false });
  };

  const stopRecording = () => {
    if (isRecordedMode) {
      stopWhisperListening();
      return;
    }
    if (interimTranscript?.trim()) {
      appendVoiceTranscript(interimTranscript);
    }
    stopDeepgramListening();
  };

  const readAloud = () => {
    const selectedText = window.getSelection()?.toString().trim();
    const textToRead = selectedText || content;
    if (!textToRead) { toast.error('Chapter is empty. Write some content first.'); return; }
    const trimmedContent = textToRead.trim();
    if (!trimmedContent) { toast.error('No text selected. Add some text to read.'); return; }
    if (trimmedContent.length < 10) { toast.error('Text is too short. Select at least a sentence to read aloud.'); return; }
    if (trimmedContent.length > 4096) {
      if (selectedText) { toast.error(`Selected text is too long (${trimmedContent.length} chars). Please select less than 4096 characters.`); return; }
      const shouldTruncate = window.confirm(`Chapter is ${trimmedContent.length} characters (limit: 4096).\n\nClick OK to read the first ~4000 characters, or Cancel to select a specific section.`);
      if (!shouldTruncate) { toast.info('💡 Tip: Select text with your cursor to read specific sections!'); return; }
      const truncated = trimmedContent.substring(0, 4000);
      const lastSentenceEnd = Math.max(truncated.lastIndexOf('.'), truncated.lastIndexOf('?'), truncated.lastIndexOf('!'));
      const finalText = lastSentenceEnd > 3000 ? truncated.substring(0, lastSentenceEnd + 1) : truncated.substring(0, 4000);
      toast.info(`Reading first ${finalText.length} characters...`);
      if (isReading) stopSpeaking(); else speak(finalText);
      return;
    }
    if (selectedText) toast.info(`Reading selected text (${trimmedContent.length} chars)...`);
    if (isReading) stopSpeaking(); else speak(textToRead);
  };

  const handleChapterSelect = (newChapterId) => {
    if (newChapterId !== chapterId) saveAndNavigate(`/chapters/${newChapterId}`);
  };

  const handleAddChapter = async () => {
    if (hasUnsavedChanges.current && chapterId) await autoSave();
    try {
      const response = await axios.post(`${API}/books/${book.book_id}/chapters`, {
        title: `Chapter ${chapters.length + 1}`, chapter_number: chapters.length + 1
      });
      toast.success('Chapter created!');
      navigate(`/chapters/${response.data.chapter_id}`);
    } catch (error) {
      console.error('Failed to create chapter:', error);
      toast.error('Failed to create chapter');
    }
  };

  const handleTextSelection = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.toString().trim().length > 2) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const x = Math.max(80, Math.min(window.innerWidth - 80, rect.left + rect.width / 2));
      const y = rect.top < 60 ? rect.bottom + 8 : rect.top - 48;
      setSelectionToolbar({ visible: true, x, y });
    } else {
      setSelectionToolbar(prev => ({ ...prev, visible: false }));
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mouseup', handleTextSelection);
    document.addEventListener('touchend', handleTextSelection);
    return () => {
      document.removeEventListener('mouseup', handleTextSelection);
      document.removeEventListener('touchend', handleTextSelection);
    };
  }, [handleTextSelection]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F5F0] dark:bg-[#1A1816]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5C4D6B] dark:border-[#F5F1E8]"></div>
      </div>
    );
  }

  const contextualItems = [
    { icon: ArrowLeft, label: 'Back to Book', onClick: () => saveAndNavigate(`/books/${book?.book_id}`), testId: 'back-to-book-button' },
    ...(book?.is_interview_mode ? [{ icon: Mic, label: 'Go to Interview', onClick: () => saveAndNavigate(`/books/${book.book_id}`), testId: 'back-to-interview-button' }] : []),
    { divider: true },
    { icon: Save, label: saving ? 'Saving...' : 'Save Chapter', onClick: handleSave, disabled: saving, spinning: saving, badge: content !== lastSavedContent && !saving, testId: 'save-chapter-button' },
    { icon: BookOpen, label: 'Chapters', onClick: () => window.innerWidth < 768 ? setMobileSidebarOpen(true) : setShowSidebar(prev => !prev) },
    { icon: Sparkles, label: loadingIgnite ? 'Loading...' : 'Writing Ideas', onClick: handleIgnite, disabled: loadingIgnite, spinning: loadingIgnite, testId: 'ignite-button' },
    { icon: isRecording ? MicOff : Mic, label: isRecording ? 'Stop Dictating' : 'Dictate', onClick: isRecording ? stopRecording : startRecording, variant: isRecording ? 'destructive' : 'default', testId: 'voice-record-button' },
    { icon: Volume2, label: isReading ? 'Stop Reading' : isLoadingVoice ? 'Loading...' : 'Read Aloud', onClick: readAloud, disabled: isLoadingVoice, spinning: isLoadingVoice, variant: isReading ? 'active' : 'default', testId: 'voice-read-button' },
    { icon: Sparkles, label: 'Writing Tools', onClick: () => window.innerWidth < 768 ? setMobilePolishOpen(true) : setShowPolishPanel(prev => !prev), testId: 'toggle-polish-panel' },
    { icon: FileText, label: 'Notes', onClick: () => setShowNotesModal(true), testId: 'notes-button' },
  ];

  return (
    <AppShell contextualItems={contextualItems} backTo={{ label: 'Book', onClick: () => saveAndNavigate(`/books/${book?.book_id}`) }} className="chapter-editor-page">

      {/* Auto-save Banner */}
      {showSavedBanner && (
        <div className="fixed top-2 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-top-2 fade-in duration-300">
          <div className="bg-green-500 text-white text-sm px-4 py-2 rounded-full shadow-lg flex items-center space-x-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Draft saved</span>
          </div>
        </div>
      )}

      {/* Mobile Sidebar Overlays */}
      <div className={`sidebar-overlay ${mobileSidebarOpen ? 'visible' : ''}`} onClick={() => setMobileSidebarOpen(false)} />
      <div className={`sidebar-overlay ${mobilePolishOpen ? 'visible' : ''}`} onClick={() => setMobilePolishOpen(false)} />

      {/* TEXT SELECTION FLOATING TOOLBAR */}
      {selectionToolbar.visible && (
        <div
          className="fixed z-30 flex items-center gap-0.5 bg-[#2A2420]/88 dark:bg-[#F0EBE4]/92 backdrop-blur-sm rounded-full px-2 py-1.5 shadow-lg"
          style={{ left: selectionToolbar.x, top: selectionToolbar.y, transform: 'translateX(-50%)' }}
        >
          <button
            onClick={() => { setSelectionToolbar(p => ({ ...p, visible: false })); handleIgnite(); }}
            className="p-1.5 rounded-full hover:bg-white/20 dark:hover:bg-black/10"
            title="Writing Ideas"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#F0EBE4] dark:text-[#2A2420]" />
          </button>
          <button
            onClick={() => { setSelectionToolbar(p => ({ ...p, visible: false })); isRecording ? stopRecording() : startRecording(); }}
            className="p-1.5 rounded-full hover:bg-white/20 dark:hover:bg-black/10"
            title={isRecording ? 'Stop Dictating' : 'Dictate'}
          >
            {isRecording ? <MicOff className="w-3.5 h-3.5 text-red-400" /> : <Mic className="w-3.5 h-3.5 text-[#F0EBE4] dark:text-[#2A2420]" />}
          </button>
          <button
            onClick={() => { setSelectionToolbar(p => ({ ...p, visible: false })); readAloud(); }}
            disabled={isLoadingVoice}
            className="p-1.5 rounded-full hover:bg-white/20 dark:hover:bg-black/10 disabled:opacity-50"
            title="Read selected text"
          >
            {isLoadingVoice ? <Loader2 className="w-3.5 h-3.5 text-[#F0EBE4] dark:text-[#2A2420] animate-spin" /> : <Volume2 className="w-3.5 h-3.5 text-[#F0EBE4] dark:text-[#2A2420]" />}
          </button>
        </div>
      )}

      {/* RECORDING FLOATING INDICATOR */}
      {isRecording && (
        <div className="fixed bottom-[5.75rem] sm:bottom-8 left-1/2 -translate-x-1/2 z-30 w-[calc(100vw-2rem)] max-w-[430px] rounded-[1.35rem] border border-[#C4B0D0]/35 dark:border-[#7B6A90]/40 bg-[#FFFCF7]/94 dark:bg-[#211E1A]/94 text-[#2A2420] dark:text-[#F0EBE4] shadow-[0_24px_70px_-44px_rgba(58,43,31,0.85)] backdrop-blur-md px-3 py-2.5">
          <div className="flex items-center gap-3 pr-16">
            <div className="flex items-center gap-2 min-w-0">
              <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#7B6A90]/45"></span>
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#7B6A90] dark:bg-[#B0A0C0]"></span>
              </span>
              <div className="min-w-0">
                <p className="text-xs font-semibold leading-tight text-[#4A3D58] dark:text-[#D8CDE5]">
                  {isTranscribingVoice ? 'Transcribing...' : 'Listening'}
                </p>
                <p className="text-[11px] leading-tight text-[#7C7065] dark:text-[#A99F95]">
                  {isTranscribingVoice ? 'Turning your voice into text' : 'Speak naturally'}
                </p>
              </div>
            </div>
            <VoiceWaveform
              audioLevel={audioLevel}
              isRecording={isRecording}
              barsCount={30}
              barMaxHeight={38}
              className="flex-1 min-w-0 overflow-hidden"
            />
          </div>
          <div className="mt-2 min-h-[1.55rem] rounded-2xl border border-[#E8DED0]/55 bg-[#FBF8F2]/68 px-3 py-1.5 pr-16 text-[12px] leading-snug text-[#5F554B] dark:border-[#302820]/60 dark:bg-[#1C1A18]/58 dark:text-[#CFC3B7]">
            {isTranscribingVoice
              ? 'Finishing transcription...'
              : isRecordedMode
                ? 'Recording backup mode. Words will appear after Stop.'
                : interimTranscript?.trim()
                  ? interimTranscript.trim()
                  : 'Live words will appear here as you speak.'}
          </div>
          <button
            onClick={stopRecording}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full border border-[#C4B0D0]/45 bg-[#F0EBE4]/88 px-3 py-1.5 text-xs font-medium text-[#4A3D58] transition-colors hover:bg-[#E8E0D4] dark:border-[#7B6A90]/45 dark:bg-[#2A2520]/88 dark:text-[#E8DFD4] dark:hover:bg-[#332B38]"
          >
            Stop
          </button>
        </div>
      )}

      {/* Main writing layout */}
      <div className="flex min-h-screen">
        {/* LEFT SIDEBAR - Chapters (Desktop) */}
        <div className={`hidden md:block ${showSidebar ? 'w-72' : 'w-0'} transition-all duration-300 overflow-hidden border-r border-[#E7DED1]/70 dark:border-[#302820]/60 bg-[#FBF8F2]/55 dark:bg-[#171411]/60 flex-shrink-0`}>
          <div className="h-full p-4">
            <ChapterSidebar
              chapters={chapters}
              currentChapterId={chapterId}
              bookTitle={book?.title}
              onChapterSelect={handleChapterSelect}
              onAddChapter={handleAddChapter}
            />
          </div>
        </div>

        {/* CENTER - Writing Area */}
        <div className="flex-1 overflow-visible md:overflow-y-auto">
          <div className="mx-auto max-w-[980px] px-0 sm:px-6 lg:px-10 py-0 sm:py-8 space-y-4 sm:space-y-5">
            {/* Main Writing Surface */}
            <div className="bg-[#FFFCF7]/96 dark:bg-[#211E1A]/92 sm:border sm:border-[#E8DED0]/70 dark:sm:border-[#302820]/70 sm:rounded-[1.35rem] overflow-hidden sm:shadow-[0_30px_90px_-66px_rgba(58,43,31,0.7)]">

              {/* SLIM COLLAPSIBLE HEADER */}
              <button
                onClick={() => setHeaderExpanded(prev => !prev)}
                className={`w-full text-left transition-all duration-200 hover:bg-[#F8F4EE]/60 dark:hover:bg-[#1C1A18]/40 ${
                  headerExpanded
                    ? 'px-5 sm:px-10 pt-8 sm:pt-10 pb-4 border-b border-[#ECE3D7]/60 dark:border-[#302820]/50 bg-[#FBF8F2]/30 dark:bg-[#1C1A18]/20'
                    : 'pl-14 pr-5 py-1.5 border-b border-[#ECE3D7]/30 dark:border-[#302820]/20'
                }`}
              >
                {headerExpanded ? (
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="mb-1 text-[11px] font-medium uppercase tracking-[0.14em] text-[#8C8176] dark:text-[#8F8174]">
                        {book?.title} · Chapter {chapter?.chapter_number}
                      </p>
                      <h1 className="font-serif text-2xl sm:text-4xl leading-tight text-[#201B17] dark:text-[#F5F1E8]">
                        {chapter?.title}
                      </h1>
                      <p className="mt-1.5 text-xs text-[#7C7065] dark:text-[#A99F95]">
                        {content !== lastSavedContent ? 'Unsaved changes' : 'Autosaved'}
                      </p>
                    </div>
                    <ChevronUp className="w-4 h-4 text-[#A09890] dark:text-[#6A6460] mt-2 flex-shrink-0" />
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[#C8C0B8] dark:text-[#454240] tracking-wide truncate flex-1">
                      {chapter?.title}
                    </span>
                    {content !== lastSavedContent && (
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400/70 flex-shrink-0" />
                    )}
                    <ChevronDown className="w-3 h-3 text-[#C8C0B8] dark:text-[#454240] flex-shrink-0" />
                  </div>
                )}
              </button>

              {/* EXPANDED: Writing Guide */}
              {headerExpanded && (
                <div className="px-5 sm:px-10 py-4 border-b border-[#ECE3D7]/60 dark:border-[#302820]/50 bg-[#FBF8F2]/20 dark:bg-[#1C1A18]/15">
                  <ChapterCoverage
                    chapterId={chapterId}
                    chapterNumber={chapter?.chapter_number || 1}
                    genre={book?.genre || 'freestyle'}
                    content={content}
                  />
                </div>
              )}

              {/* Search Result Banner */}
              {highlightQuery && (
                <div className="px-3 sm:px-4 py-2 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800">
                  <p className="text-xs sm:text-sm text-yellow-800 dark:text-yellow-200">
                    🔍 Search result: <strong>"{highlightQuery}"</strong>
                  </p>
                </div>
              )}

              {/* OUTDATED POLISH BANNER */}
              {polishState === 'outdated' && polishedContent && (
                <div className="px-3 sm:px-5 py-3 bg-[#FBF4E8] dark:bg-[#282116] border-b border-[#E8D8BD] dark:border-[#463724]">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-xl">📝</span>
                      <div>
                        <p className="text-sm font-semibold text-[#6F4A1D] dark:text-[#E7C68D]">Your draft changed</p>
                        <p className="text-xs text-[#8C6A3A] dark:text-[#D7BB88]">The polished preview below is based on your previous version.</p>
                      </div>
                    </div>
                    <Button
                      onClick={() => handlePolish('pro', 'medium')}
                      disabled={polishing}
                      size="sm"
                      className="rounded-full bg-[#5C4D6B] hover:bg-[#4A3D58] text-white font-medium"
                      data-testid="repolish-banner-btn"
                    >
                      {polishing ? (
                        <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Re-Polishing...</>
                      ) : (
                        <><RefreshCw className="w-4 h-4 mr-1" />Re-Polish Now</>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Voice shortcuts */}
              <div className="px-5 sm:px-10 pt-4 pb-1 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={isRecording ? stopRecording : startRecording}
                  className={`h-8 min-w-8 inline-flex items-center justify-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-all shadow-[0_12px_34px_-30px_rgba(58,43,31,0.78)] ${
                    isRecording
                      ? 'border-[#7B6A90]/60 bg-[#5C4D6B] text-white hover:bg-[#4A3D58] dark:bg-[#7B6A90] dark:hover:bg-[#6B5A80]'
                      : 'border-[#E8DED0]/68 bg-[#FBF8F2]/62 text-[#5D5248] hover:bg-[#F0EBE4] hover:text-[#2A2420] dark:border-[#302820]/70 dark:bg-[#1C1A18]/46 dark:text-[#B8AEA4] dark:hover:bg-[#2A2520]/70 dark:hover:text-[#F0EBE4]'
                  }`}
                  title={isRecording ? 'Stop dictating' : 'Dictate'}
                  aria-label={isRecording ? 'Stop dictating' : 'Dictate'}
                  data-testid="canvas-voice-record-button"
                >
                  {isRecording ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                  <span className="hidden min-[380px]:inline">{isRecording ? 'Stop' : 'Dictate'}</span>
                </button>
                <button
                  type="button"
                  onClick={readAloud}
                  disabled={isLoadingVoice}
                  className={`h-8 min-w-8 inline-flex items-center justify-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-all shadow-[0_12px_34px_-30px_rgba(58,43,31,0.78)] disabled:cursor-not-allowed ${
                    isReading
                      ? 'border-[#7B6A90]/60 bg-[#5C4D6B] text-white hover:bg-[#4A3D58] dark:bg-[#7B6A90] dark:hover:bg-[#6B5A80]'
                      : isLoadingVoice
                        ? 'border-[#E8DED0]/60 bg-[#FBF8F2]/54 text-[#7C7065] dark:border-[#302820]/64 dark:bg-[#1C1A18]/42 dark:text-[#A99F95]'
                        : 'border-[#E8DED0]/68 bg-[#FBF8F2]/62 text-[#5D5248] hover:bg-[#F0EBE4] hover:text-[#2A2420] dark:border-[#302820]/70 dark:bg-[#1C1A18]/46 dark:text-[#B8AEA4] dark:hover:bg-[#2A2520]/70 dark:hover:text-[#F0EBE4]'
                  }`}
                  title={isReading ? 'Stop reading' : 'Read aloud'}
                  aria-label={isReading ? 'Stop reading' : 'Read aloud'}
                  data-testid="canvas-voice-read-button"
                >
                  {isLoadingVoice ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />}
                  <span className="hidden min-[380px]:inline">{isReading ? 'Stop' : isLoadingVoice ? 'Loading' : 'Read'}</span>
                </button>
              </div>

              {/* Textarea */}
              <Textarea
                ref={textareaRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Start writing your chapter here..."
                data-testid="chapter-content-textarea"
                className="min-h-screen sm:min-h-[680px] p-5 sm:px-10 sm:py-9 border-none focus:ring-0 bg-transparent text-[#2A2420] placeholder:text-[#B8AEA4] dark:text-[#F0EBE4] dark:placeholder-[#F5F1E8]/40 resize-none mobile-editor"
                style={{
                  fontFamily: 'Libre Baskerville, Georgia, "Times New Roman", serif',
                  fontSize: '19px',
                  lineHeight: '1.92'
                }}
              />

              {/* Word Count Bar */}
              <div className="px-5 sm:px-10 py-2.5 border-t border-[#ECE3D7]/70 dark:border-[#302820]/60 bg-[#FBF8F2]/62 dark:bg-[#1C1A18]/56">
                <WordCountBar content={content} />
              </div>
            </div>

            {/* Original Content Viewer (collapsible) */}
            {originalContent && originalContent !== content && (
              <div className="bg-[#F8F5F0] dark:bg-[#22201C] border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-xl sm:rounded-2xl overflow-hidden">
                <button
                  onClick={() => setShowOriginal(!showOriginal)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-[#E8E0D4]/50 dark:hover:bg-[#2A2520]/50 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <FileText className="w-4 h-4 text-[#7C766E]" />
                    <span className="font-medium text-sm text-[#2A2420] dark:text-[#F0EBE4]">Your Original Words</span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${showOriginal ? 'rotate-180' : ''}`} />
                </button>
                {showOriginal && (
                  <div className="px-4 pb-4">
                    <p className="text-xs text-gray-500 mb-2">This is what you originally wrote - preserved exactly as you said it.</p>
                    <div
                      className="bg-white dark:bg-[#1A1816] rounded-lg p-4 text-[#2A2420] dark:text-[#F0EBE4] whitespace-pre-wrap max-h-60 overflow-y-auto border border-[#E0D8CC] dark:border-[#2A2520]"
                      style={{ fontFamily: 'Libre Baskerville, Georgia, serif', fontSize: '15px', lineHeight: '1.7' }}
                    >
                      {originalContent}
                    </div>
                    <Button
                      onClick={() => { setContent(originalContent); toast.success('Restored original text'); }}
                      variant="outline"
                      size="sm"
                      className="mt-3 text-xs"
                    >
                      Restore Original
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Polished Preview (if exists) - READ-ONLY */}
            {polishedContent && (
              <div className={`relative border rounded-xl sm:rounded-2xl p-4 sm:p-6 ${
                polishState === 'outdated'
                  ? 'bg-[#FBF4E8] dark:bg-[#282116] border-[#E8D8BD] dark:border-[#463724]'
                  : 'bg-[#FFFCF7] dark:bg-[#211E1A] border-[#E7DED1]/80 dark:border-[#302820]/80'
              }`}>
                <div className="absolute top-2 right-2 px-2 py-0.5 bg-[#F8F5F0]/80 dark:bg-[#1A1816]/80 rounded text-xs text-gray-500 dark:text-gray-400">
                  Read-only Preview
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className={`font-semibold ${polishState === 'outdated' ? 'text-[#6F4A1D] dark:text-[#E7C68D]' : 'text-[#2A2420] dark:text-[#F0EBE4]'}`}>
                      Refined Preview
                    </h3>
                    {polishState === 'outdated' && (
                      <span className="px-2 py-0.5 bg-amber-200 dark:bg-amber-800/50 text-amber-800 dark:text-amber-200 text-xs rounded-full">Needs Update</span>
                    )}
                    <Button
                      onClick={() => { if (isReading || isLoadingVoice) { stopSpeaking(); } else if (polishedContent?.trim()) { speak(polishedContent); } else { toast.error('No polished text to read'); } }}
                      variant="outline"
                      size="sm"
                      disabled={isLoadingVoice || !polishedContent?.trim()}
                      className={`${isReading ? 'bg-[#E8E0D4] dark:bg-[#2A2520]/60 border-[#C4B0D0]' : isLoadingVoice ? 'bg-[#F0EBE4] border-[#E0D8CC]' : 'border-[#E0D8CC] dark:border-[#2A2520]'} text-[#5C4D6B] dark:text-[#B0A0C0]`}
                    >
                      {isLoadingVoice ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Volume2 className="w-4 h-4 mr-1" />}
                      {isReading ? 'Stop' : isLoadingVoice ? 'Loading...' : 'Read'}
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={usePolishedVersion} size="sm" className="bg-[#5C4D6B] hover:bg-[#4A3D58] dark:bg-[#7B6A90] dark:hover:bg-[#6B5A80] text-white flex-1 sm:flex-none">
                      Use This Version
                    </Button>
                    {getNextChapter() && (
                      <Button onClick={() => handleAcceptAndNextChapter(polishedContent)} size="sm" className="bg-green-600 hover:bg-green-700 text-white flex-1 sm:flex-none" data-testid="accept-next-center">
                        Accept & Next →
                      </Button>
                    )}
                  </div>
                </div>
                {polishState === 'outdated' && (
                  <p className="text-xs text-amber-700 dark:text-amber-300 mb-3 flex items-center">
                    <span className="mr-1">📝</span> You've made changes. Click Re-Polish to refresh the refined version.
                  </p>
                )}
                <div className="select-text cursor-default" style={{ fontFamily: 'Libre Baskerville, Georgia, serif', fontSize: '16px', lineHeight: '1.75' }}>
                  <p className={`whitespace-pre-wrap ${polishState === 'outdated' ? 'text-[#6F4A1D]/80 dark:text-[#E7C68D]/80' : 'text-[#3D3530] dark:text-[#E8DFD4]'}`}>
                    {polishedContent}
                  </p>
                </div>
              </div>
            )}

            {/* DEPTH EXPANSION SUGGESTIONS */}
            {polishedContent && (
              <div className="mt-4 p-4 sm:p-6 bg-white dark:bg-[#22201C] rounded-xl sm:rounded-2xl border border-[#E0D8CC] dark:border-[#2A2520]/50" data-testid="depth-suggestions-card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-[#2A2420] dark:text-[#F0EBE4] flex items-center">
                    <Sparkles className="w-4 h-4 mr-2 text-[#5C4D6B] dark:text-[#B0A0C0]" />
                    Deepen Your Story
                    {loadingSuggestions && <Loader2 className="w-4 h-4 ml-2 animate-spin text-[#5C4D6B]" />}
                  </h3>
                  {editorialSuggestions.length > 0 && (
                    <span className="px-2 py-1 bg-[#E8E0D4] dark:bg-[#2A2520] text-[#5C4D6B] dark:text-[#B0A0C0] text-xs rounded-full font-medium">
                      {editorialSuggestions.length} suggestions
                    </span>
                  )}
                </div>
                {loadingSuggestions && editorialSuggestions.length === 0 && (
                  <div className="text-center py-6">
                    <Loader2 className="w-8 h-8 animate-spin text-[#5C4D6B] mx-auto mb-2" />
                    <p className="text-sm text-[#7C766E] dark:text-[#8C847A]">Analyzing your writing...</p>
                  </div>
                )}
                {!loadingSuggestions && editorialSuggestions.length === 0 && (
                  <div className="text-center py-4">
                    <p className="text-sm text-[#7C766E] dark:text-[#8C847A]">✨ Your writing is rich with detail!</p>
                  </div>
                )}
                {editorialSuggestions.length > 0 && (
                  <>
                    <p className="text-sm text-[#7C766E] dark:text-[#8C847A] mb-4 bg-[#F8F5F0] dark:bg-[#1C1A18] p-3 rounded-lg">
                      These moments in your <strong className="text-[#2A2420] dark:text-[#F0EBE4]">original draft</strong> could be expanded.
                    </p>
                    <div className="space-y-3">
                      {editorialSuggestions.map((suggestion, idx) => (
                        <div key={idx} className="p-4 bg-[#F8F5F0] dark:bg-[#1C1A18] rounded-xl border border-[#E0D8CC] dark:border-[#2A2520]/50 hover:border-[#C4B0D0]/60 transition-all">
                          <div className="flex items-start gap-2 mb-3">
                            <span className="text-[#A09890] text-base">❝</span>
                            <p className="text-sm text-[#3D3530] dark:text-[#E8DFD4] italic flex-1">
                              {suggestion.quoted_text?.substring(0, 150)}{suggestion.quoted_text?.length > 150 ? '...' : ''}
                            </p>
                            <span className="text-[#A09890] text-base">❞</span>
                          </div>
                          {suggestion.why_this_moment_matters && (
                            <p className="text-xs text-[#7C766E] dark:text-[#8C847A] mb-3 pl-3 border-l-2 border-[#C4B0D0]">
                              {suggestion.why_this_moment_matters}
                            </p>
                          )}
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-[#5C4D6B] dark:text-[#B0A0C0] flex items-center">
                              <span className="mr-2">→</span>{suggestion.expansion_direction}
                            </p>
                            <Button onClick={() => goToQuotedSection(suggestion)} size="sm" className="bg-[#5C4D6B] hover:bg-[#4A3D58] dark:bg-[#7B6A90] dark:hover:bg-[#6B5A80] text-white" data-testid={`expand-suggestion-${idx}`}>
                              <ArrowRight className="w-4 h-4 mr-1" />Go & Expand
                            </Button>
                          </div>
                          {suggestion.narrative_reason && (
                            <p className="text-xs text-[#7C766E] dark:text-[#8C847A] mt-2 pt-2 border-t border-[#E0D8CC] dark:border-[#2A2520]/50">
                              {suggestion.narrative_reason}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT SIDEBAR - Polish & Notes (Desktop only, fixed overlay so it's immune to flex reflow) */}
        <div className={`hidden md:block fixed right-0 top-0 h-screen w-[360px] z-30 border-l border-[#E7DED1]/70 dark:border-[#302820]/60 bg-[#FBF8F2]/97 dark:bg-[#171411]/97 overflow-y-auto transition-transform duration-300 ${showPolishPanel ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-[#ECE3D7]/80 dark:border-[#302820]/70">
              <div className="flex items-center space-x-2">
                <Sparkles className="w-4 h-4 text-[#756958] dark:text-[#B8AEA4]" />
                <span className="font-semibold text-[#2A2420] dark:text-[#F0EBE4] text-sm">Writing tools</span>
              </div>
              <Button onClick={() => setShowPolishPanel(false)} variant="ghost" size="sm" className="rounded-full text-[#7C7065] hover:bg-[#F7F1E9] hover:text-[#2A2420] dark:text-[#A99F95] dark:hover:bg-[#29241F] dark:hover:text-[#F0EBE4]">
                <PanelRightClose className="w-4 h-4" />
              </Button>
            </div>
            <PolishStyles
              content={content}
              onPolish={handlePolish}
              polishedContent={polishedContent}
              isPolishing={polishing}
              polishState={polishState}
              onUsePolished={(polished) => { setContent(polished); setPolishedContent(null); setPolishState('none'); }}
              onAcceptAndNext={handleAcceptAndNextChapter}
              hasNextChapter={!!getNextChapter()}
            />
            <QuickNotes chapterId={chapterId} initialNotes={notes} onSave={handleNotesSave} />
          </div>
        </div>
      </div>

      {/* Mobile Left Sidebar - Chapters */}
      <div className={`editor-sidebar md:hidden bg-[#FBF8F2] dark:bg-[#171411] border-r border-[#E7DED1] dark:border-[#302820] ${mobileSidebarOpen ? 'open' : ''}`}>
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif font-bold text-[#2A2420] dark:text-[#F0EBE4]">Chapters</h3>
            <Button variant="ghost" size="sm" onClick={() => setMobileSidebarOpen(false)}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          <ChapterSidebar
            chapters={chapters}
            currentChapterId={chapterId}
            bookTitle={book?.title}
            onChapterSelect={(id) => { setMobileSidebarOpen(false); saveAndNavigate(`/chapters/${id}`); }}
            onAddChapter={async () => { setMobileSidebarOpen(false); await handleAddChapter(); }}
          />
        </div>
      </div>

      {/* Mobile Right Sidebar - Tools */}
      <div className={`editor-sidebar editor-sidebar-right md:hidden bg-[#FBF8F2] dark:bg-[#171411] border-l border-[#E7DED1] dark:border-[#302820] ${mobilePolishOpen ? 'open' : ''}`}>
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Sparkles className="w-4 h-4 text-[#756958] dark:text-[#B8AEA4]" />
              <h3 className="font-semibold text-[#2A2420] dark:text-[#F0EBE4] text-sm">Writing tools</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setMobilePolishOpen(false)}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          <PolishStyles
            content={content}
            onPolish={(style, depth) => { handlePolish(style, depth); }}
            polishedContent={polishedContent}
            isPolishing={polishing}
            polishState={polishState}
            onUsePolished={(polished) => { setContent(polished); setPolishedContent(null); setPolishState('none'); setMobilePolishOpen(false); }}
            onAcceptAndNext={(polished) => { handleAcceptAndNextChapter(polished); setMobilePolishOpen(false); }}
            hasNextChapter={!!getNextChapter()}
          />
          <QuickNotes chapterId={chapterId} initialNotes={notes} onSave={handleNotesSave} />
        </div>
      </div>

      {/* Freeze Detector */}
      <FreezeDetector content={content} genre={book?.genre || 'freestyle'} chapterId={chapterId} isRecording={isRecording} />

      {/* Ignite Modal */}
      {showIgniteModal && igniteData && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowIgniteModal(false)}>
          <div className="bg-white dark:bg-[#22201C] rounded-xl max-w-md w-full max-h-[85vh] overflow-y-auto shadow-xl border border-[#E0D8CC] dark:border-[#2A2520]/50" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#F8F5F0] dark:bg-[#1C1A18] border-b border-[#E0D8CC] dark:border-[#2A2520]/50 px-4 py-3 flex items-center justify-between rounded-t-xl">
              <div className="flex items-center space-x-2">
                <Sparkles className="w-4 h-4 text-[#5C4D6B] dark:text-[#B0A0C0]" />
                <h2 className="text-sm font-semibold text-[#2A2420] dark:text-[#F0EBE4]">Writing Ideas</h2>
              </div>
              <button onClick={() => setShowIgniteModal(false)} className="text-[#7C766E] hover:text-[#2A2420] dark:hover:text-[#F0EBE4] hover:bg-[#E8E0D4]/60 dark:hover:bg-[#2A2520]/50 rounded-lg p-1.5 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {igniteData.starter_sentence && (
                <div className="bg-[#F8F5F0] dark:bg-[#1C1A18] rounded-lg p-4 border border-[#E0D8CC] dark:border-[#2A2520]/50">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Feather className="w-4 h-4 text-[#5C4D6B] dark:text-[#B0A0C0]" />
                        <h3 className="text-sm font-semibold text-[#2A2420] dark:text-[#F0EBE4]">Opening Line</h3>
                      </div>
                      <p className="text-[#3D3530] dark:text-[#C8BFB4] italic text-sm leading-relaxed">"{igniteData.starter_sentence}"</p>
                    </div>
                    <Button onClick={() => { insertTextAtCursor(igniteData.starter_sentence); setShowIgniteModal(false); }} size="sm" className="bg-[#5C4D6B] hover:bg-[#4A3D58] dark:bg-[#7B6A90] dark:hover:bg-[#6B5A80] text-white shrink-0 text-xs px-3 py-1.5">
                      Insert
                    </Button>
                  </div>
                </div>
              )}
              {igniteData.brain_igniters?.length > 0 && (
                <div>
                  <div className="flex items-center space-x-2 mb-3">
                    <Lightbulb className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">Writing Prompts</h3>
                  </div>
                  <div className="space-y-2">
                    {igniteData.brain_igniters.map((question, index) => (
                      <div key={index} className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors cursor-pointer border border-green-200 dark:border-green-800"
                        onClick={() => { insertTextAtCursor(`\n${question}\n\n`); toast.success('Question inserted!'); }}>
                        <p className="text-gray-800 dark:text-gray-200 text-sm">{question}</p>
                        <p className="text-xs text-green-700 dark:text-green-400 mt-1.5">Click to insert</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {igniteData.craft_insight && (
                <div className="bg-[#F8F5F0] dark:bg-[#1C1A18] rounded-lg p-4 border border-[#E0D8CC] dark:border-[#2A2520]/50">
                  <div className="flex items-center space-x-2 mb-2">
                    <Target className="w-4 h-4 text-[#5C4D6B] dark:text-[#B0A0C0]" />
                    <h3 className="text-sm font-semibold text-[#2A2420] dark:text-[#F0EBE4]">Writing Tip</h3>
                  </div>
                  <p className="text-[#3D3530] dark:text-[#C8BFB4] text-sm">{igniteData.craft_insight}</p>
                </div>
              )}
              <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-[#2A2520]/50">
                <Button onClick={() => setShowIgniteModal(false)} variant="outline">Close</Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Notes Modal */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowNotesModal(false)}>
          <div className="bg-white dark:bg-[#22201C] rounded-xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-xl border border-[#E0D8CC] dark:border-[#2A2520]/50" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#F8F5F0] dark:bg-[#1C1A18] border-b border-[#E0D8CC] dark:border-[#2A2520]/50 px-4 py-3 flex items-center justify-between rounded-t-xl">
              <div className="flex items-center space-x-2">
                <FileText className="w-4 h-4 text-[#5C4D6B] dark:text-[#B0A0C0]" />
                <h2 className="text-sm font-semibold text-[#2A2420] dark:text-[#F0EBE4]">Notes</h2>
              </div>
              <button onClick={() => setShowNotesModal(false)} className="text-[#7C766E] hover:text-[#2A2420] dark:hover:text-[#F0EBE4] hover:bg-[#E8E0D4]/60 dark:hover:bg-[#2A2520]/50 rounded-lg p-1.5 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4">
              <QuickNotes chapterId={chapterId} initialNotes={notes} onSave={handleNotesSave} />
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

export default ChapterEditorPage;
